import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import { readStamp } from '../FigureEditor/svg/figureSvg';
/**
 * payloadToLexical — AI 산출 Block/Run payload → Lexical editorState (**그리기 전용**).
 *
 * 왜 있나: 변형(⑤)·생성(⑥)의 산출은 staging **Block/Run**(`{stem, choices, box, …}`)인데,
 * 문제은행에 등록된 뒤의 같은 문제는 **Lexical**(`body_lexical`)이다. 그래서 검수 화면과
 * [문제 보기] 탭이 서로 다른 렌더러로 그려져, 같은 문제가 승인 전후로 다른 얼굴이었다 —
 * 글자 크기·선지 배치·수식 배율이 전부 갈렸다. 여기서 앞의 것을 뒤의 모양으로 바꿔,
 * 검수도 은행과 **같은 부품**(OcrLexicalEditor)으로 그린다.
 *
 * ⚠ **저장하지 않는다.** 검수 저장(PATCH)이 보내는 것은 여전히 원본 Block/Run 이다
 *   (`VariationReviewCard.buildPatch` 의 `patch.problem`). 이 파일이 만든 editorState 는
 *   화면에만 살고 서버로 나가지 않는다 — 승인 시 Lexical 을 만드는 것은 서버다.
 *   그러므로 여기 규칙이 서버 변환과 조금 달라도 **데이터가 어긋나지 않는다.** 어긋나는 것은
 *   미리 보는 얼굴뿐이고, 그 얼굴을 서버 규칙에 맞추려고 아래 표를 그대로 옮겼다.
 *
 * 변환 규칙 — `BE-ref-ocr-editor-backend-ready.md` §Q4(확정·구현·검증됨):
 *   | choices + choices_columns≥2 | layout-container{templateColumns:"1fr"×n} > layout-item |
 *   | choices (columns 1/없음)    | 세로 문단(①~⑤ 접두)                                    |
 *   | box                         | box > box-content                                       |
 *   | table                       | table > tablerow > tablecell{colSpan,rowSpan,headerState}|
 *   | list                        | list{listType} > listitem                               |
 *   | image                       | paragraph(format:center) > image{src,…}                 |
 *   | math(독립)                  | paragraph > equation{inline:false}                      |
 * 그리고 선지 마커 ①②③ 는 **equation 노드**다 — 기존 문제은행 23,304 문서의 표준형이고
 * (FE-20260825-reply-batch.md), 평문으로 두면 KaTeX 배율(1.21em)이 빠져 동그라미가 작아 보인다.
 *
 * 입력 스키마는 §Q1 이 정본이지만 **읽기는 관대하다** — BlockRenderer 와 같은 방침이다
 * (run: t|type|kind + v|value|text|content, block: runs|content|children). 모르는 것은
 * 버리지 않고 원문으로 강등한다: 검수 화면에서 조용히 사라지는 것이 가장 나쁘다.
 */

import { splitSourceLatex } from './sourceLatex';
import { boxMarkerPrefix, boxGroups, boxEntries, boxAnchor } from '../OcrHub/boxRules';

/** 승격 후 문제은행의 그림 관례와 같은 값(§Q2: maxWidth 500·caption 포함) */
const IMAGE_MAX_WIDTH = 500;

/**
 * 수식을 감싼 구분자를 벗긴다.
 *
 * 계약(§Q1)의 math run 은 **bare LaTeX** 지만, 산출을 만드는 것은 모델이고 같은 문제의
 * 원본 평문(`source_text`)은 `\(...\)` 형식이다(sourceLatex.js 가 그 실측 위에 서 있다).
 * 구분자가 붙은 채로 KaTeX 에 넘어가면 `\(` 가 모르는 명령이 되어 **수식이 통째로 빨간 글자**가
 * 된다 — 검수자가 눈으로 확인할 수 없는 바로 그 상태다.
 *
 * **전체를 감싼 한 쌍만** 벗긴다. 문장 중간의 달러는 손대지 않는다(금액과 구분할 길이 없다).
 */
export function stripMathDelimiters(raw) {
  let s = String(raw ?? '').trim();
  // 한 겹씩, 더 벗길 것이 없을 때까지 — `$\(x\)$` 처럼 겹쳐 오는 산출을 본 적이 있다
  for (let i = 0; i < 3; i += 1) {
    const before = s;
    if (s.startsWith('\\(') && s.endsWith('\\)')) s = s.slice(2, -2).trim();
    else if (s.startsWith('\\[') && s.endsWith('\\]')) s = s.slice(2, -2).trim();
    else if (s.startsWith('$$') && s.endsWith('$$') && s.length >= 4) s = s.slice(2, -2).trim();
    else if (s.startsWith('$') && s.endsWith('$') && s.length >= 2) s = s.slice(1, -1).trim();
    if (s === before) break;
  }
  return s;
}

const emptyRoot = () => ({
  children: [], direction: null, format: '', indent: 0, type: 'root', version: 1,
});

/** ElementNode 계열 공통 껍데기 */
const el = (type, children, extra = null) => ({
  children: Array.isArray(children) ? children : [],
  direction: 'ltr',
  format: '',
  indent: 0,
  type,
  version: 1,
  ...(extra || {}),
});

const textNode = (t) => ({
  detail: 0, format: 0, mode: 'normal', style: '', text: String(t), type: 'text', version: 1,
});

/** equation 노드 — inline 이면 글 흐름 안, 아니면 제 줄을 쓰는 독립 수식 */
const equationNode = (latex, inline) => ({
  equation: stripMathDelimiters(latex), inline: !!inline, type: 'equation', version: 1,
});

/**
 * 글자 run 안에 **수식이 통째로 박혀 온 경우**를 건져 낸다.
 *
 * 모델이 `{kind:'text', value:'값 \\(x^2\\) 를 구하시오'}` 처럼 한 덩어리로 내는 일이 있다.
 * 그러면 수식이 run 으로 갈라지지 않아 **LaTeX 원문이 글자 그대로** 화면에 남는다 —
 * 「수식이 안 나온다」의 다른 얼굴이다.
 *
 * 나누는 규칙은 원본 대조(SourceTextView)와 **같은 것**을 쓴다(splitSourceLatex): `\(...\)`·`\[...\]`
 * 둘만 보고 `$...$` 는 보지 않는다 — 본문에는 달러가 금액으로도 오고, 한 번의 오탐이 그 뒤
 * 문장을 통째로 수식으로 삼킨다. 위아래(원본↔결과)가 같은 규칙이라야 대조도 어긋나지 않는다.
 */
function textToNodes(str) {
  const s = String(str ?? '');
  if (s === '') return [];
  if (!s.includes('\\(') && !s.includes('\\[')) return [textNode(s)];
  return splitSourceLatex(s).flatMap((p) => {
    if (!p.math) return p.value === '' ? [] : [textNode(p.value)];
    // `\[...\]` 는 디스플레이 수식이라 제 줄을 쓴다
    return [equationNode(p.value, !p.raw.startsWith('\\['))];
  });
}

const paragraph = (children, format = '') => el('paragraph', children, { format });

/**
 * image 노드 — caption 은 **빈 중첩 editorState** 여야 한다.
 * ImageNode.importJSON 이 `caption.editorState` 를 parseEditorState 하므로, 이 키가 없으면
 * 그림 하나 때문에 문서 전체가 열리지 않는다(ImageNode.tsx:106).
 */
const imageNode = (src, altText = '', width = null, height = null) => ({
  altText: String(altText || ''),
  caption: { editorState: { root: emptyRoot() } },
  // 0 은 Lexical 에서 'inherit' 이다. 서버가 준 픽셀 크기가 있으면 그것을 싣는다 —
  // 은행 관례가 width/height 를 갖고(§Q2), 지면 미리보기는 그 절대 픽셀에 배율을 먹인다.
  height: Number(height) > 0 ? Number(height) : 0,
  maxWidth: IMAGE_MAX_WIDTH,
  showCaption: false,
  src: String(src),
  ...(FIGURE_SVG_EDITOR ? { ...(readStamp(src) || {}), format: readStamp(src) ? 'svg' : 'pixel' } : {}),
  type: 'image',
  version: 1,
  width: Number(width) > 0 ? Number(width) : 0,
});

/**
 * 그림의 픽셀이 실려 온 자리를 찾는다.
 *
 * 정본 스키마(§Q2)의 image 블록은 `{kind:'image', bbox}` **뿐**이라 그릴 픽셀이 없다 —
 * OCR 검수는 페이지 presigned 를 bbox 로 크롭해 메우지만(BlockRenderer 의 PageImageContext),
 * 변형·생성 산출에는 그 페이지가 없다. 대신 서버가 그림을 **재부착**하거나(`image_mode:'reuse'`)
 * **새로 렌더**하므로(`figspec`), 어느 키로 오든 놓치지 않게 알려진 자리를 모두 본다.
 * 하나도 없으면 null 이고, 그때는 호출부가 「[그림]」 자리표시로 강등한다.
 */
function pickImageSrc(block) {
  // `svg` 가 정본이다 — BE 부록 2026-08-30: image 블록에 spec 이 있으면 서버가 렌더한 SVG 를
  // data URI 로 실어 준다(읽기 시 계산, 저장본은 spec 만). 렌더 실패면 `svg:null` + `issues` 라
  // 여기서 걸러지고 아래 자리표시 경로로 내려간다.
  // 나머지는 그 부록 이전부터 방어적으로 보던 자리다 — 옛 산출을 놓치지 않으려고 남긴다.
  const cands = [...(FIGURE_SVG_EDITOR ? [block?.src, block?.svg] : [block?.svg, block?.src]), block?.data_uri, block?.dataUri, block?.url, block?.image_url];
  const hit = cands.find((v) => typeof v === 'string' && v.trim() !== '');
  return hit || null;
}

/**
 * 그리지 못한 도판의 사유 — BE 부록 2026-08-30: `svg:null` 일 때 같은 블록에 `issues:[…]` 가 온다.
 * 최상위 `result.image_issues`(계약 §4 D4)와 같은 일을 하지만 **어느 블록인지가 자명**하다.
 * 화면이 이것을 안 읽으면 「[그림]」 글자만 남은 산출이 이유 없이 승인 대기로 남는다.
 */
export function collectImageIssues(payload) {
  const out = [];
  let index = 0;
  const walkBlocks = (blocks) => {
    (Array.isArray(blocks) ? blocks : []).forEach((b) => {
      if (b == null || typeof b !== 'object') return;
      const kind = String(b.kind ?? b.type ?? '');
      if (kind.includes('image')) {
        const i = index;
        index += 1;
        if (!pickImageSrc(b) && Array.isArray(b.issues) && b.issues.length > 0) {
          out.push({ index: i, issues: b.issues.map(String), ...(FIGURE_SVG_EDITOR && b.figure_ref ? { figure_ref: b.figure_ref } : {}) });
        }
        return;
      }
      if (kind === 'table') {
        (Array.isArray(b.rows) ? b.rows : []).forEach((row) => {
          (Array.isArray(row) ? row : []).forEach((cell) => walkBlocks(cell?.content));
        });
        return;
      }
      if (kind === 'list') {
        (Array.isArray(b.items) ? b.items : []).forEach(walkBlocks);
        return;
      }
      // 문단 안 run 자리에 섞여 온 image 도 센다(변환기가 그 자리도 그린다)
      const runs = runsOf(b);
      (Array.isArray(runs) ? runs : []).forEach((r) => {
        if (r == null || typeof r !== 'object') return;
        if (!String(r.kind ?? r.t ?? r.type ?? '').includes('image')) return;
        const i = index;
        index += 1;
        if (!pickImageSrc(r) && Array.isArray(r.issues) && r.issues.length > 0) {
          out.push({ index: i, issues: r.issues.map(String), ...(FIGURE_SVG_EDITOR && r.figure_ref ? { figure_ref: r.figure_ref } : {}) });
        }
      });
    });
  };
  if (!payload || typeof payload !== 'object') return out;
  walkBlocks(payload.stem);
  (Array.isArray(payload.box) ? payload.box : []).forEach((b) => walkBlocks(b?.content));
  (Array.isArray(payload.choices) ? payload.choices : []).forEach((c) => walkBlocks(c?.content));
  return out;
}

/**
 * Lexical editorState 안의 image 노드 `src` 를 **문서 순서로** 모은다.
 *
 * 쓰는 곳: `image_mode:'reuse'` 인 변형의 검수 화면. 그 변형은 원본 그림을 **그대로 재부착**해
 * 등록되는데(계약 §4), 검수 단계의 산출 블록에는 픽셀이 없어 「[그림]」 자리표시만 남는다 —
 * 그림이 있는 문제를 그림 없이 검수하게 되는 것이다. 원본에서 같은 순서로 빌려 채운다.
 *
 * 세는 규칙은 은행 쪽과 같다(`$dfs` 가 닿는 `type:'image'` 전부, 캡션 제외 — BE-20260829 §4 Q1).
 */
export function collectLexicalImageSrcs(editorStateStr) {
  let root = null;
  try { root = JSON.parse(String(editorStateStr))?.root; } catch (_) { return []; }
  const out = [];
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'image') {
      if (typeof node.src === 'string' && node.src !== '') out.push(node.src);
      return; // caption 안으로는 들어가지 않는다
    }
    (Array.isArray(node.children) ? node.children : []).forEach(walk);
  };
  walk(root);
  return out;
}

/**
 * 원본에서 빌려 올 그림 목록 — 진입 함수가 세우고 끝에서 지운다.
 *
 * 변환은 **동기**라 재진입이 없고, 그래서 카운터를 인자로 모든 재귀 함수에 실어 나르는 대신
 * 여기 둔다. 자리는 **image 블록의 문서 순서**로 매긴다(픽셀이 있든 없든 한 칸씩) — 그래야
 * n번째 그림이 원본의 n번째 그림과 짝이 된다.
 */
let imageFallbacks = null;
function nextImageFallback(hasOwnPixels) {
  if (!imageFallbacks) return null;
  const i = imageFallbacks.i;
  imageFallbacks.i += 1;
  if (hasOwnPixels) return null;
  const v = imageFallbacks.list[i];
  return typeof v === 'string' && v !== '' ? v : null;
}

/** run 하나 → 인라인 노드 배열(0개 이상) */
function runToNodes(run) {
  if (run == null) return [];
  if (typeof run === 'string') return textToNodes(run);

  const kind = String(run.kind ?? run.t ?? run.type ?? 'text');

  if (kind.includes('latex') || kind.includes('math')) {
    const latex = run.latex ?? run.v ?? run.value ?? run.text ?? run.content ?? '';
    // 구분자를 벗기고 나서 비면 수식이 아니었다 — 빈 equation 은 화면에서 자리만 먹는다
    return stripMathDelimiters(latex) === '' ? [] : [equationNode(latex, true)];
  }
  if (kind.includes('blank')) {
    // 빈칸은 (가)/(나) 표식으로 — BlockRenderer·백엔드 텍스트 변환과 같은 표기
    const label = run.label ?? run.v ?? run.value ?? '';
    return [textNode(`( ${String(label)} )`)];
  }
  if (kind.includes('image')) {
    const own = pickImageSrc(run);
    // 자리는 **언제나** 한 칸 소비한다 — `||` 로 미루면 제 픽셀이 있는 그림에서 칸이 안 넘어가
    // 뒤따르는 그림이 한 칸씩 밀린다(단축 평가로 호출 자체가 건너뛰어진다)
    const borrowed = nextImageFallback(!!own);
    const src = own || borrowed;
    return [src ? imageNode(src, run.alt ?? run.altText ?? '', run.width, run.height) : textNode('[그림]')];
  }

  const value = run.v ?? run.value ?? run.text ?? run.content;
  if (value == null) {
    // 모르는 run — 소실시키지 않고 원문으로 강등한다(BlockRenderer 와 같은 원칙)
    return [textNode(JSON.stringify(run))];
  }
  return textToNodes(value);
}

const runsToNodes = (runs) => (Array.isArray(runs) ? runs : []).flatMap(runToNodes);

/** 블록에서 run 배열을 꺼낸다 — 계약은 `runs` 지만 옛 산출이 content·children 으로도 온다 */
function runsOf(block) {
  if (Array.isArray(block?.runs)) return block.runs;
  if (Array.isArray(block?.content)) return block.content;
  if (Array.isArray(block?.children)) return block.children;
  return null;
}

/** 표 — table > tablerow > tablecell. 셀 안은 다시 블록이라 재귀한다 */
function tableToNode(block) {
  const rows = Array.isArray(block.rows) ? block.rows : [];
  if (rows.length === 0) return null;
  return el('table', rows.map((cells) => el(
    'tablerow',
    (Array.isArray(cells) ? cells : []).map((cell) => el(
      'tablecell',
      blocksToNodes(cell?.content),
      {
        // headerState 1 = ROW(머리 셀). @lexical/table 0.11 의 값이다
        headerState: cell?.header ? 1 : 0,
        colSpan: cell?.colSpan > 1 ? cell.colSpan : 1,
        rowSpan: cell?.rowSpan > 1 ? cell.rowSpan : 1,
        backgroundColor: null,
        width: undefined,
      },
    )),
    { height: undefined },
  )));
}

/** 목록 — list{listType} > listitem */
function listToNode(block) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (items.length === 0) return null;
  const ordered = !!block.ordered;
  return el('list', items.map((blocks, i) => el('listitem', blocksToNodes(blocks), { value: i + 1 })), {
    listType: ordered ? 'number' : 'bullet',
    start: 1,
    tag: ordered ? 'ol' : 'ul',
  });
}

/** 블록 하나 → 블록 노드 배열(0개 이상) */
function blockToNodes(block) {
  if (block == null) return [];
  if (typeof block === 'string') return [paragraph(textToNodes(block))];

  const kind = String(block.kind ?? block.type ?? '');

  if (kind.includes('math')) {
    const latex = block.latex ?? block.v ?? block.value ?? '';
    // 빈 수식은 아래 runs 폴백으로 흘려 보낸다 — 계약 위반 형태를 빈 줄로 삼키지 않게.
    // 판정은 **구분자를 벗긴 뒤** 한다: `\(\)` 는 글자로는 비어 있지 않지만 그릴 것이 없다.
    if (stripMathDelimiters(latex) !== '') return [paragraph([equationNode(latex, false)])];
  }
  if (kind.includes('image')) {
    const own = pickImageSrc(block);
    // 픽셀이 없으면 원본에서 같은 자리의 그림을 빌린다(image_mode:'reuse' — 계약 §4).
    // 자리는 픽셀 유무와 무관하게 한 칸 소비한다(위 run 자리와 같은 이유).
    const borrowed = nextImageFallback(!!own);
    const src = own || borrowed;
    // 승격 규칙과 같이 가운데 정렬 문단에 담는다
    if (src) return [paragraph([imageNode(src, block.alt ?? block.altText ?? '', block.width, block.height)], 'center')];
    return [paragraph([textNode('[그림]')], 'center')];
  }
  if (kind === 'table') {
    const t = tableToNode(block);
    if (t) return [t];
    return [paragraph([textNode(JSON.stringify(block))])];
  }
  if (kind === 'list') {
    const l = listToNode(block);
    if (l) return [l];
    return [paragraph([textNode(JSON.stringify(block))])];
  }
  // 발문 안 상자 — 모델이 최상위 box 대신 stem 안 제자리에 낸 모양(서버 대장 B1, prod 24건).
  // 종전엔 모르는 블록으로 원문(JSON) 강등됐다. 그 자리에 box > box-content 로 세운다(서버 _box_node 와 같게).
  if (kind === 'box') {
    const b = boxGroupToNode([block]);
    return b ? [b] : [];
  }

  const runs = runsOf(block);
  if (runs) return [paragraph(runsToNodes(runs))];

  // 모르는 블록 — 원문 강등(검수 가능 수준 우선)
  return [paragraph([textNode(JSON.stringify(block))])];
}

function blocksToNodes(blocks) {
  return (Array.isArray(blocks) ? blocks : []).flatMap(blockToNodes);
}

/**
 * 상자 항목 하나(마커 + 내용) → 노드들. 규칙은 서버 lexical_convert 와 같다(boxRules):
 *   · 마커가 이미 구두점으로 끝나면 공백만(「ㄱ.」→「ㄱ. 」, 「(가)」→「(가) 」), 없으면 접두 없음
 *   · 빈 문단은 뺀다 — 서버 _blocks_to_paragraphs 가 빼므로, 남기면 마커가 빈 문단에 붙어 줄이 어긋난다
 */
function boxEntryNodes(entry) {
  const nodes = blocksToNodes(entry?.content)
    .filter((n) => !(n.type === 'paragraph' && (!Array.isArray(n.children) || n.children.length === 0)));
  const prefix = boxMarkerPrefix(entry?.marker);
  if (!prefix) return nodes;
  const mark = textNode(prefix);
  if (nodes.length === 0) return [paragraph([mark])];
  const [first, ...rest] = nodes;
  if (first.type === 'paragraph') return [{ ...first, children: [mark, ...first.children] }, ...rest];
  return [paragraph([mark]), ...nodes];
}

/** 상자 묶음 하나 → box > box-content 노드(그릴 것이 없으면 null). box 는 반드시 box-content 를 품는다 */
function boxGroupToNode(group) {
  const inner = boxEntries(group).flatMap(boxEntryNodes);
  return inner.length > 0 ? el('box', [el('box-content', inner)]) : null;
}

/** 원문자 마커 — 번호는 선지의 no 를 따른다(위치로 매기면 BlockRenderer 와 어긋난다) */
const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
const markerOf = (no, i) => CIRCLED[(no ?? i + 1) - 1] || `(${no ?? i + 1})`;

/**
 * 선지 하나 → 블록 노드 배열. 마커는 **첫 문단의 맨 앞**에 equation 으로 얹는다.
 * 첫 블록이 문단이 아니면(표·그림으로 시작하는 선지) 마커만 있는 문단을 앞에 세운다 —
 * 표 노드 안에 인라인 노드를 넣으면 Lexical 이 문서를 열지 못한다.
 */
function choiceToNodes(choice, i) {
  const mark = equationNode(markerOf(choice?.no, i), true);
  const nodes = blocksToNodes(choice?.content);
  if (nodes.length === 0) return [paragraph([mark])];
  const [first, ...rest] = nodes;
  if (first.type === 'paragraph') {
    return [{ ...first, children: [mark, textNode(' '), ...first.children] }, ...rest];
  }
  return [paragraph([mark]), ...nodes];
}

/**
 * 본문 payload → editorState JSON 문자열. 그릴 것이 하나도 없으면 null.
 *
 * 순서는 지면과 같다: 발문(stem) → <보기> 상자(box) → 선지(choices).
 * answerType·points 는 넣지 않는다 — 검수 카드가 배지로 따로 그리고, 은행의 본문에도 없다.
 */
export function bodyPayloadToLexical(payload, { fallbackImages = null } = {}) {
  if (!payload || typeof payload !== 'object') return null;
  imageFallbacks = Array.isArray(fallbackImages) && fallbackImages.length > 0
    ? { list: fallbackImages, i: 0 }
    : null;
  try {
    return buildBody(payload);
  } finally {
    // 다음 호출이 남은 카운터를 물려받지 않게 반드시 지운다
    imageFallbacks = null;
  }
}

function buildBody(payload) {
  const children = [];

  // 발문을 블록마다 **먼저** 바꾼다 — 그림 자리 빌려오기(nextImageFallback)가 서버의 그림 순번
  // (발문 → 상자 → 선지)과 같은 차례로 소비되게. 상자를 발문 가운데로 옮겨도 소비 차례는 그대로다.
  const stem = Array.isArray(payload.stem) ? payload.stem : [];
  const stemNodes = stem.map(blockToNodes);

  // 최상위 상자 — 구형(마커 항목들)은 한 상자, 신형은 항목마다 한 상자(서버 box_groups)
  const groups = boxGroups(payload.box);
  const boxNodes = groups.map(boxGroupToNode).filter(Boolean);
  // 「…다음 조건을 만족시킨다.」 뒤에 질문 문단이 이어지면 상자를 그 도입 문장 바로 뒤로(서버 box_anchor — 대장 B9).
  // 번호는 발문의 객체 블록만 센 것이라 원래 배열 위치로 되돌린다.
  const anchor = boxNodes.length > 0 ? boxAnchor(stem, groups) : null;
  const objIdx = stem.map((b, i) => (b != null && typeof b === 'object' && !Array.isArray(b) ? i : -1)).filter((i) => i >= 0);
  const cut = anchor == null ? null : objIdx[anchor];
  stemNodes.forEach((ns, i) => {
    children.push(...ns);
    if (i === cut) children.push(...boxNodes);
  });
  if (cut == null) children.push(...boxNodes);

  const choices = Array.isArray(payload.choices) ? payload.choices : [];
  if (choices.length > 0) {
    const cols = Number(payload.choices_columns);
    if (Number.isFinite(cols) && cols >= 2) {
      const n = Math.min(5, Math.trunc(cols));
      children.push(el(
        'layout-container',
        choices.map((c, i) => el('layout-item', choiceToNodes(c, i))),
        { templateColumns: Array(n).fill('1fr').join(' ') },
      ));
    } else {
      choices.forEach((c, i) => children.push(...choiceToNodes(c, i)));
    }
  }

  if (children.length === 0) return null;
  return JSON.stringify({ root: { ...emptyRoot(), direction: 'ltr', children } });
}

/**
 * 풀이 payload → editorState JSON 문자열. 그릴 것이 없으면 null.
 * `final_value` 는 은행 풀이에 그런 칸이 없으므로 마지막 문단으로 이어 붙인다.
 */
export function solutionPayloadToLexical(payload, { fallbackImages = null } = {}) {
  if (!payload || typeof payload !== 'object') return null;
  imageFallbacks = Array.isArray(fallbackImages) && fallbackImages.length > 0
    ? { list: fallbackImages, i: 0 }
    : null;
  try {
    const children = blocksToNodes(payload.content);
    const fv = payload.final_value;
    if (fv != null && String(fv) !== '') {
      children.push(paragraph([textNode(`최종값: ${String(fv)}`)]));
    }
    if (children.length === 0) return null;
    return JSON.stringify({ root: { ...emptyRoot(), direction: 'ltr', children } });
  } finally {
    imageFallbacks = null;
  }
}

export default bodyPayloadToLexical;
